import React, { useState, useEffect } from "react";
import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
import "./Content.css";
import { getValidRoutes, isPublicRoute } from "../../config/routeConfig";
import Home from "../home/Home";
import CallAutomation from "../callautomation/CallAutomationTest";
import CallHistory from "../callhistory/CallHistory";
import CallHistoryPage from "../call-history-page";
import AuthLogin from "../login/auth-login";
import ProtectedRoute from "../../utils/ProtectedRoute";
import SamlSuccess from "../saml/SamlSuccess";
import AuthSuccess from "../auth/AuthSuccess";
import AuthError from "../auth/AuthError";
import Facility from "../facility/Facility";
import EHRManagement from "../ehr-management/EHRManagement";
import {
  getAccountDetails,
  insertUserActivity,
} from "../../utils/CustomerUiAPI";
import {
  fetchAndCacheCSEKKey,
  getCachedCSEKKey,
} from "../../utils/secured-nexus-bridge";
import { useAccount } from "../../context/AccountContext";
import UnauthorizedPage from "../unautorized-page/UnauthorizedPage";
import CallAutomationV2 from "../callautomation-v2";
import InitialStep from "../callautomation-v2/steps/InitialStep";
import QuestionSelectionStep from "../callautomation-v2/steps/QuestionSelectionStep";
import FileUploadStep from "../callautomation-v2/steps/FileUploadStep";
import UserManagement from '../userManagement/UserManagement';
import Provider from "../provider/Provider";
import Department from "../department/Department";
import DepartmentDetails from "../department/DepartmentDetails";
import Billing from "../billing/Billing";
import ChangePassword from '../login/ChangePassword';
import UserProfile from '../userProfile/UserProfile';
import SsoSetup from "../settings/SsoSetup";
import Onboarding from "../onboarding/Onboarding";
import QuestionManagement from '../question-management';
import FaxAgent from "../faxAgent/FaxAgent";
import BuyFaxNumber from "../faxAgent/fax-number/BuyFaxNumber";
import FaxDetails from "../faxAgent/FaxDetails";
import ApiDocs from "../api-docs";

const Content = ({ isAuthenticated, setIsAuthenticated, selectMenu }) => {
  const [forceLoginDialogVisible, setForceLoginDialogVisible] = useState(false);
  const [noSessionDialogVisible, setNoSessionDialogVisible] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  const { setAccountDetails } = useAccount();

  const loginValidate = async (
    userMail,
    activityType,
    forceFlagLogin,
    sessionId,
    type
  ) => {
    try {
      const res = await insertUserActivity(
        userMail,
        activityType,
        forceFlagLogin,
        sessionId
      );
      if (res.statusCode === "SUCCESS") {
        sessionStorage.setItem("name", res.response.username);
        sessionStorage.setItem("permissions", res.response.permissions);
        sessionStorage.setItem("role", res.response.role);
        sessionStorage.setItem("userStatus", "notWarned");
        await fetchAndCacheCSEKKey();
        const csekKey = await getCachedCSEKKey();
        const accountRes = await getAccountDetails(csekKey);
        setAccountDetails(accountRes.accountDetails);
        setIsAuthenticated(true);
        navigate("/home");
        // window.location.reload();
        // sessionStorage.setItem(
        //   "latestActivePayment",
        //   JSON.stringify(accountRes.latestActivePayment)
        // );
      }
    } catch (error) {
      if (error?.response?.status === 506) {
        setForceLoginDialogVisible(true);
      } else if (error?.response?.status === 510) {
        setNoSessionDialogVisible(true);
      }
      console.error(error);
    }
  };

  useEffect(() => {
    if (location.pathname.startsWith("/authenticate")) {
      const params = new URLSearchParams(window.location.search);
      const provider = params.get("provider");
      const idToken = params.get("idToken");
      const sessionId = params.get("sessionId");
      const refreshToken = params.get("refreshToken");
      const expiresIn = params.get("expiresIn");
      const basicToken = params.get("authentication_token");
      const email = params.get("email");

      // Save tokens and email in sessionStorage
      if (idToken) sessionStorage.setItem("idToken", idToken);
      if (refreshToken) sessionStorage.setItem("refreshToken", refreshToken);
      if (expiresIn) sessionStorage.setItem("expiresIn", expiresIn);
      if (basicToken) sessionStorage.setItem("BasicToken", basicToken);
      if (email) sessionStorage.setItem("mail", email);
      if (sessionId) {
        sessionStorage.setItem("sessionId", sessionId);
        localStorage.setItem("sessionId", sessionId);
      }
      // If no email is provided, alert and redirect to the login/home page.
      if (!email) {
        alert("Email does not exist. Please contact support.");
        window.history.replaceState({}, document.title, "/");
        navigate("/");
        return;
      }

      // Trigger loginValidate with the extracted values.
      loginValidate(email, "loginTime", "True", sessionId, "login")
        .then(() => {
          window.history.replaceState({}, document.title, "/");
          navigate("/home");
        })
        .catch((error) => {
          console.error("Error during login validation:", error);
          alert("Login validation failed. Please try again.");
        });
    }
  }, [location, navigate, loginValidate]);

  useEffect(() => {
    // Parse the current URL query parameters
    const urlParams = new URLSearchParams(window.location.search);
    const errorParam = urlParams.get("error");

    if (errorParam) {
      // Show an alert with the error message
      alert(errorParam);

      // Remove 'error' from the query params
      urlParams.delete("error");

      // Update the browser URL, removing the 'error' param
      // If there are no remaining query parameters, this will end up
      // just being the pathname (e.g. /).
      const newQueryString = urlParams.toString();
      const newUrl = newQueryString
        ? `${window.location.pathname}?${newQueryString}`
        : window.location.pathname;

      window.history.replaceState({}, "", newUrl);
    }
  }, [location, navigate]);

  useEffect(() => {
    const handleStorageChange = () => {
      setIsAuthenticated(localStorage.getItem("isAuthenticated") === "true");
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [setIsAuthenticated]);

  // Add cleanup function for initial load
  const cleanupStorage = () => {
    const isFirstLogin = localStorage.getItem('isFirstLogin');
    const theme = localStorage.getItem('theme');
    localStorage.clear();
    sessionStorage.clear();
    if (isFirstLogin) {
      localStorage.setItem('isFirstLogin', isFirstLogin);
    }
    if(theme) {
      localStorage.setItem('theme', theme);
    }
  };

  // Add effect for initial load cleanup
  useEffect(() => {
    if (location.pathname === '/') {
      cleanupStorage();
    }
  }, [location.pathname]);

  // Add effect for access token validation
  useEffect(() => {
    const accessToken = sessionStorage.getItem('access_token');
    // Don't redirect OAuth success/error pages as they need to process auth data first
    const isOAuthRoute = location.pathname.startsWith('/auth/');
    // Check if current route is public (doesn't require authentication)
    const isCurrentRoutePublic = isPublicRoute(location.pathname);
    
    console.log('🔍 Auth Check:', {
      pathname: location.pathname,
      hasAccessToken: !!accessToken,
      isOAuthRoute,
      isPublicRoute: isCurrentRoutePublic,
      isAuthenticated
    });
    
    // If no access token and route requires authentication, redirect to login
    if (!accessToken && !isCurrentRoutePublic && !isOAuthRoute) {
      console.log('❌ No access token found, redirecting to login from:', location.pathname);
      navigate('/');
    }
  }, [location.pathname, navigate, isAuthenticated]);

  // Add catch-all route handler
  useEffect(() => {
    const validRoutes = getValidRoutes();

    // Normalize pathname by removing duplicate slashes and trailing slashes
    const normalizedPathname = location.pathname.replace(/\/+/g, '/').replace(/\/$/, '') || '/';

    console.log('locationPathnameDebugger', location.pathname);
    console.log('normalizedPathname', normalizedPathname);

    // Special handling for onboarding routes - they're always valid if user is authenticated
    const isOnboardingPath = normalizedPathname.startsWith('/onboarding/');
    
    // Check if the normalized path exactly matches any valid route or is a sub-route of a valid route
    const isValidRoute = isOnboardingPath || validRoutes.some(route => {
      // For root path, only exact match
      if (route === '/') {
        return normalizedPathname === '/';
      }
      // For other routes, check if path starts with the route and is followed by either nothing or a slash
      return normalizedPathname === route || 
             normalizedPathname.startsWith(`${route}/`) || 
             normalizedPathname.startsWith(route);
    });

    console.log('isValidRoute:', isValidRoute, 'isOnboardingPath:', isOnboardingPath);

    // If the original pathname is different from normalized (has double slashes) 
    // and the normalized path is valid, redirect to the corrected URL
    if (location.pathname !== normalizedPathname && isValidRoute) {
      console.log('Redirecting to normalized path:', normalizedPathname);
      const correctedUrl = normalizedPathname + location.search + location.hash;
      window.history.replaceState({}, '', correctedUrl);
      navigate(normalizedPathname + location.search, { replace: true });
      return;
    }

    if (!isValidRoute) {
      console.log('Redirecting to / due to invalid route');
      navigate('/');
    }
  }, [location.pathname, location.search, location.hash, navigate]);

  return (
    <div className="content-root">
      <Routes>
        <Route
          path="/"
          element={
            <AuthLogin
              setIsAuthenticated={setIsAuthenticated}
              forceLoginDialogVisible={forceLoginDialogVisible}
              setForceLoginDialogVisible={setForceLoginDialogVisible}
              loginValidate={loginValidate}
              noSessionDialogVisible={noSessionDialogVisible}
              setNoSessionDialogVisible={setNoSessionDialogVisible}
            />
          }
        />
        <Route
          path="/become-a-customer"
          element={
            <AuthLogin
              setIsAuthenticated={setIsAuthenticated}
              forceLoginDialogVisible={forceLoginDialogVisible}
              setForceLoginDialogVisible={setForceLoginDialogVisible}
              loginValidate={loginValidate}
              noSessionDialogVisible={noSessionDialogVisible}
              setNoSessionDialogVisible={setNoSessionDialogVisible}
            />
          }
        />
         <Route
          path="/change-password"
          element={
            <ProtectedRoute
            element={ChangePassword}
            isAuthenticated={isAuthenticated}
            // requiredPermission="view_user_management"
            redirectTo="/"
          />}
        />
        <Route
          path="/home"
          element={
            <ProtectedRoute
              element={Home}
              isAuthenticated={isAuthenticated}
              // requiredPermission="view_call_home"
              redirectTo="/"
            />
          }
        />
        <Route
          path="/call-automation"
          element={
            <ProtectedRoute
              element={CallAutomation}
              isAuthenticated={isAuthenticated}
              // requiredPermission="view_call_automation"
              redirectTo="/"
            />
          }
        />
        <Route
          path="/call-automation-v2/*"
          element={
            <ProtectedRoute
              element={CallAutomationV2}
              isAuthenticated={isAuthenticated}
              // requiredPermission="view_call_automation"
              redirectTo="/"
            />
          }
        >
          <Route index element={<InitialStep />} />
          <Route
            path="question-selection"
            element={<QuestionSelectionStep />}
          />
          <Route path="file-upload" element={<FileUploadStep />} />
        </Route>
        {/* TEMPORARY: Old Call History route commented out */}
        {/* <Route
          path="/call-history"
          element={
            <ProtectedRoute
              element={CallHistory}
              isAuthenticated={isAuthenticated}
              // requiredPermission="view_call_history"
              redirectTo="/"
            />
          }
        /> */}
        <Route
          path="/call-history"
          element={
            <ProtectedRoute
              element={CallHistoryPage}
              isAuthenticated={isAuthenticated}
              // requiredPermission="view_call_history"
              redirectTo="/"
            />
          }
        />
        <Route
          path="/question-management"
          element={
            <ProtectedRoute
              element={QuestionManagement}
              isAuthenticated={isAuthenticated}
              // requiredPermission="view_question_management"
              redirectTo="/"
            />
          }
        />

        <Route
          path="/fax-agent"
          element={
            <ProtectedRoute
              element={FaxAgent}
              isAuthenticated={isAuthenticated}
              redirectTo="/"
            />
          }
        />
        <Route
          path="/fax-agent/create-template"
          element={
            <ProtectedRoute
              element={FaxAgent}
              isAuthenticated={isAuthenticated}
              redirectTo="/"
            />
          }
        />
        <Route
          path="/fax-agent/received-fax/:faxId"
          element={
            <ProtectedRoute
              element={FaxDetails}
              isAuthenticated={isAuthenticated}
              redirectTo="/"
            />
          }
        />
        <Route
          path="/fax-agent/buy-fax-number"
          element={
            <ProtectedRoute
              element={BuyFaxNumber}
              isAuthenticated={isAuthenticated}
              redirectTo="/"
            />
          }
        />

        <Route
          path="/facility"
          element={
            <ProtectedRoute
              element={Facility}
              isAuthenticated={isAuthenticated}
              // requiredPermission="view_facility"
              redirectTo="/"
            />
          }
        />
        <Route
          path="/ehr-management"
          element={
            <ProtectedRoute
              element={EHRManagement}
              isAuthenticated={isAuthenticated}
              redirectTo="/"
            />
          }
        />

        <Route
          path="/success"
          element={<SamlSuccess setIsAuthenticated={setIsAuthenticated} />}
        />
        <Route
          path="/auth/success"
          element={<AuthSuccess setIsAuthenticated={setIsAuthenticated} />}
        />
        <Route
          path="/auth/error"
          element={<AuthError />}
        />
        <Route
          path="/user-management"
          element={
            <ProtectedRoute
              element={UserManagement}
              isAuthenticated={isAuthenticated}
              // requiredPermission="view_user_management"
              redirectTo="/"
            />
          }
        />
        <Route
          path="/providers"
          element={
            <ProtectedRoute
              element={Provider}
              isAuthenticated={isAuthenticated}
              // requiredPermission="view_user_management"
              redirectTo="/"
            />
          }
        />
        <Route
          path="/departments"
          element={
            <ProtectedRoute
              element={Department}
              isAuthenticated={isAuthenticated}
              // requiredPermission="view_department_management"
              redirectTo="/"
            />
          }
        />
        <Route
          path="/departments/:id"
          element={
            <ProtectedRoute
              element={DepartmentDetails}
              isAuthenticated={isAuthenticated}
              // requiredPermission="view_department_management"
              redirectTo="/"
            />
          }
        />
        <Route
          path="/billing"
          element={
            <ProtectedRoute
              element={Billing}
              isAuthenticated={isAuthenticated}
              // requiredPermission="view_user_management"
              redirectTo="/"
            />
          }
        />
        <Route
          path="/user-profile"
          element={
            <ProtectedRoute
              element={UserProfile}
              isAuthenticated={isAuthenticated}
              // requiredPermission="view_user_profile"
              redirectTo="/"
            />
          }
        />
        <Route
          path="/403"
          element={<UnauthorizedPage selectMenu={selectMenu} />}
        />
        <Route
          path="/sso-setup"
          element={
            <ProtectedRoute
              element={SsoSetup}
              isAuthenticated={isAuthenticated}
              redirectTo="/"
            />
          }
        />
        <Route
          path="/onboarding/*"
          element={
            <ProtectedRoute
              element={Onboarding}
              isAuthenticated={isAuthenticated}
              redirectTo="/"
            />
          }
        />
        {/* Temporary direct test route */}
        <Route
          path="/test-onboarding/*"
          element={<Onboarding />}
        />
        <Route
          path="/api-docs"
          element={
            <ProtectedRoute
              element={ApiDocs}
              isAuthenticated={isAuthenticated}
              redirectTo="/"
              additionalCheck={() => {
                // Check if customer has access to API Documentation
                const customerId = sessionStorage.getItem('customerId');
                // Import dynamically to avoid circular dependencies
                const { hasApiDocsAccess } = require('../../config/routeConfig');
                const hasAccess = hasApiDocsAccess(customerId);
                if (!hasAccess) {
                  console.log('❌ API Documentation access denied for customer:', customerId);
                }
                return hasAccess;
              }}
            />
          }
        />
      </Routes>
    </div>
  );
};

export default Content;
